import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            type: 'bot',
            content: 'Hello! I\'m your JDS Hospitals assistant. How can I help you today?',
            timestamp: new Date()
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    // Auto-open chatbot after 5 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsOpen(true);
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const addMessage = (type, content) => {
        setMessages(prev => [...prev, {
            type,
            content,
            timestamp: new Date()
        }]);
    };

    const simulateTyping = (callback) => {
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            callback();
        }, 1000 + Math.random() * 2000);
    };

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!inputMessage.trim()) return;

        const userMessage = inputMessage.trim();
        addMessage('user', userMessage);
        setInputMessage('');

        // Process the message and generate response
        processMessage(userMessage.toLowerCase());
    };

    const processMessage = (message) => {
        simulateTyping(() => {
            let response = '';

            // Appointment booking
            if (message.includes('book') || message.includes('appointment') || message.includes('schedule')) {
                response = getAppointmentResponse(message);
            }
            // Doctor consultation suggestions
            else if (message.includes('consult') || message.includes('doctor') || message.includes('specialist') ||
                message.includes('see') || message.includes('visit')) {
                response = getDoctorSuggestion(message);
            }
            // Timing information
            else if (message.includes('time') || message.includes('timing') || message.includes('hours') ||
                message.includes('open') || message.includes('close')) {
                response = getTimingResponse(message);
            }
            // General hospital information
            else if (message.includes('emergency') || message.includes('urgent')) {
                response = 'For emergencies, please call our 24/7 hotline at 08914052667 or visit our emergency department immediately. We have advanced life support systems and experienced medical staff available round the clock.';
            }
            else if (message.includes('location') || message.includes('address') || message.includes('where')) {
                response = 'JDS Hospital is located at Seethammadara, Visakhapatnam – 530013. We\'re easily accessible by public transport and have ample parking facilities.';
            }
            else if (message.includes('contact') || message.includes('phone') || message.includes('call')) {
                response = 'You can reach us at:\n📞 Phone: 08912717711 / 08912551697\n🚑 Emergency: 08914052667\n📧 Email: info@jdshospitals.com\n🌐 Instagram: @jdshospitals';
            }
            else if (message.includes('services') || message.includes('departments')) {
                response = 'Our services include: Cardiology, Oncology, Neurology, Orthopedics, Pediatrics, Emergency Care, ICU, Surgery, Radiology, Laboratory, Pharmacy, and many more specialized treatments.';
            }
            else if (message.includes('insurance') || message.includes('payment')) {
                response = 'We accept all major health insurance plans including government schemes. We also offer flexible payment options, EMI facilities, and financial assistance programs for patients in need.';
            }
            // Greeting responses
            else if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
                response = 'Hello! Welcome to JDS Hospitals. I\'m here to help you with any questions about our services, appointments, or general information. What would you like to know?';
            }
            else if (message.includes('thank') || message.includes('thanks')) {
                response = 'You\'re welcome! Is there anything else I can help you with regarding JDS Hospitals?';
            }
            // Default response
            else {
                response = 'I\'m here to help you with information about JDS Hospitals. I can assist with:\n\n• Booking appointments\n• Doctor/specialist recommendations\n• Hospital timings and services\n• Contact information\n• Emergency procedures\n\nWhat specific information are you looking for?';
            }
            addMessage('bot', response);
        });
    };
    const getAppointmentResponse = (message) => {
        if (message.includes('book') || message.includes('schedule')) {
            return 'I\'d be happy to help you book an appointment! To get started, could you please tell me:\n\n1. What type of consultation do you need? (e.g., General Physician, Cardiology, Pediatrics, etc.)\n2. Your preferred date and time\n3. Any specific symptoms or concerns\n\nAlternatively, you can call our appointment line at +91-22-XXXX-XXXX or book online through our website.';
        }
        return 'For appointment-related queries, I can help you understand our booking process. We offer both online booking and phone booking. Our specialists are available Monday to Saturday, 9 AM to 7 PM. Would you like me to guide you through the booking process?';
    };
    const getDoctorSuggestion = (message) => {
        if (message.includes('heart') || message.includes('cardiac') || message.includes('chest pain')) {
            return 'For heart-related concerns, I recommend consulting with our Cardiology department. Dr. Priya Sharma is our senior cardiologist specializing in preventive cardiology and heart health. For emergencies, please visit our 24/7 emergency department immediately.';
        }
        else if (message.includes('child') || message.includes('pediatric') || message.includes('baby') || message.includes('kid')) {
            return 'For children\'s health concerns, our Pediatrics department is excellent. Dr. Meera Patel is our pediatric specialist with extensive experience in child healthcare, vaccinations, and developmental issues.';
        }
        else if (message.includes('cancer') || message.includes('oncology') || message.includes('tumor')) {
            return 'For oncology concerns, Dr. Vikram Singh leads our Oncology department. We offer comprehensive cancer care including chemotherapy, radiation therapy, immunotherapy, and personalized treatment plans.';
        }
        else if (message.includes('bone') || message.includes('joint') || message.includes('orthopedic')) {
            return 'For bone and joint issues, our Orthopedics department can help. We have specialists in sports medicine, joint replacement, and fracture care.';
        }
        else if (message.includes('mental') || message.includes('stress') || message.includes('anxiety') || message.includes('depression')) {
            return 'For mental health concerns, Dr. Anjali Mehta heads our Psychiatry department. We provide comprehensive mental health services in a supportive, confidential environment.';
        }
        else if (message.includes('skin') || message.includes('dermatology')) {
            return 'For skin-related concerns, our Dermatology department offers comprehensive skin care services including treatment for various skin conditions, cosmetic procedures, and skin cancer screening.';
        }
        else if (message.includes('eye') || message.includes('vision') || message.includes('ophthalmology')) {
            return 'For eye care, our Ophthalmology department provides comprehensive eye examinations, cataract surgery, glaucoma treatment, and vision correction services.';
        }
        else if (message.includes('dental') || message.includes('teeth')) {
            return 'For dental care, our Dentistry department offers preventive care, restorative procedures, oral surgery, and cosmetic dentistry services.';
        }
        else {
            return 'Based on your concern, I recommend starting with a consultation with our General Physician for initial assessment. Dr. Rajesh Kumar is our experienced GP who can evaluate your symptoms and refer you to the appropriate specialist if needed. Would you like to book an appointment?';
        }
    };

    const getTimingResponse = (message) => {
        if (message.includes('emergency')) {
            return 'Our Emergency Department operates 24/7, 365 days a year. We have advanced life support systems and experienced medical staff available round the clock for any medical emergencies.';
        }
        else if (message.includes('pharmacy')) {
            return 'Our pharmacy is open:\n• Monday to Saturday: 8:00 AM to 10:00 PM\n• Sunday: 9:00 AM to 6:00 PM\n• Emergency medications available 24/7 through our emergency department.';
        }
        else if (message.includes('lab') || message.includes('laboratory') || message.includes('test')) {
            return 'Our laboratory services are available:\n• Monday to Saturday: 7:00 AM to 8:00 PM\n• Sunday: 8:00 AM to 2:00 PM\n• Emergency testing available 24/7.';
        }
        else {
            return 'Our general timings are:\n• OPD Hours: Monday to Saturday, 9:00 AM to 7:00 PM\n• Emergency Services: 24/7\n• Pharmacy: 8:00 AM to 10:00 PM (Mon-Sat), 9:00 AM to 6:00 PM (Sun)\n• Laboratory: 7:00 AM to 8:00 PM (Mon-Sat), 8:00 AM to 2:00 PM (Sun)\n\nPlease note that specialist consultation hours may vary.';
        }
    };

    return (
        <>
            {/* Chatbot Toggle Button */}
            <div className="chatbot-toggle" onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? (
                    <span className="close-icon">×</span>
                ) : (
                    <div className="chat-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM9 9C9.55 9 10 8.55 10 8C10 7.45 9.55 7 9 7C8.45 7 8 7.45 8 8C8 8.55 8.45 9 9 9ZM15 9C15.55 9 16 8.55 16 8C16 7.45 15.55 7 15 7C14.45 7 14 7.45 14 8C14 8.55 14.45 9 15 9ZM8 13H16V15H8V13Z" fill="white" />
                        </svg>
                    </div>
                )}
            </div>

            {/* Chatbot Window */}
            {isOpen && (
                <div className="chatbot-container">
                    <div className="chatbot-header">
                        <div className="chatbot-header-info">
                            <div className="chatbot-avatar">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4L19 9Z" fill="#0e59a6" />
                                </svg>
                            </div>
                            <div>
                                <h4>JDS Hospitals Assistant</h4>
                                <span className="status">● Online</span>
                            </div>
                        </div>
                        <button className="minimize-btn" onClick={() => setIsOpen(false)}>−</button>
                    </div>

                    <div className="chatbot-messages">
                        {messages.map((message, index) => (
                            <div key={index} className={`message ${message.type}`}>
                                <div className="message-content">
                                    {message.content.split('\n').map((line, i) => (
                                        <div key={i}>{line}</div>
                                    ))}
                                </div>
                                <div className="message-time">
                                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="message bot typing">
                                <div className="message-content">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="chatbot-input" onSubmit={handleSendMessage}>
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            placeholder="Type your message..."
                            disabled={isTyping}
                        />
                        <button type="submit" disabled={isTyping || !inputMessage.trim()}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="white" />
                            </svg>
                        </button>
                    </form>
                </div>
            )}
        </>
    );
};

export default Chatbot;
